import { Fade, useScrollTrigger } from "@mui/material";
import React, { useRef } from "react";
import { getWindowPositioning } from "@/helpers/utils";

export interface StickyProps {
  displayThreshold?: number;
  anchor?: "top" | "bottom";
  inPageFlow?: boolean;
  timeout?: number;
  children: React.ReactElement;
}

export default function Sticky({
  displayThreshold,
  children,
  anchor = "top",
  inPageFlow = false,
  timeout = 100,
}: StickyProps) {
  const ref = useRef<HTMLDivElement>(null);
  const scrollTrigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: displayThreshold,
    ...(inPageFlow &&
      ref.current && {
        threshold:
          getWindowPositioning(ref.current).top + (displayThreshold ?? 0),
      }),
  });
  const position = !inPageFlow || scrollTrigger ? "fixed" : "relative";

  const stickyHeight = ref.current
    ? getWindowPositioning(ref.current).bottom -
      getWindowPositioning(ref.current).top
    : 0;

  const stickyContent = (
    <div data-id="sticky-container" ref={ref}>
      <div
        style={{
          zIndex: 1154,
          width: "100%",
          position: position,
          overflow: "hidden",
          pointerEvents: "auto",
          top: anchor === "top" ? 0 : "auto",
          bottom: anchor === "top" ? "auto" : 0,
          transition: "box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
          ...(anchor === "bottom" && {
            filter:
              "drop-shadow(0px -4px 4px hsl(var(--color-gray-900) / 0.25))",
          }),
          ...(position === "fixed" && {
            boxShadow:
              "0px 2px 4px -1px rgb(0 0 0 / 20%), 0px 4px 5px 0px rgb(0 0 0 / 14%), 0px 1px 10px 0px rgb(0 0 0 / 12%)",
          }),
        }}
      >
        {children}
      </div>
    </div>
  );
  return inPageFlow ? (
    <>
      {stickyContent}
      {position === "fixed" && (
        <div data-id="sticky-spacer" style={{ height: stickyHeight }}></div>
      )}
    </>
  ) : (
    <Fade in={!displayThreshold || scrollTrigger} timeout={timeout}>
      {stickyContent}
    </Fade>
  );
}
